import { useTranslation } from 'react-i18next'
import { shallowEqual, useSelector } from 'react-redux'
import { format } from 'date-fns'

import { v4 as uuidv4 } from 'uuid'
import {
  Table,
  TableBody,
  Box,
  TableContainer,
  alpha,
  Button,
} from '@mui/material'
import { colors } from '@/theme/colors.theme'

import PaginationTable from '@/views/components/pagination-table/index.component'
import SkeletonLoader from '@/views/components/skeleton'
import NoContentAvailable from '@/views/components/no-content-available/index.component'

import useRoundsReports from '@/views/reports/hooks/use-reports-type-rounds.hooks'
import useReportsFilters from '@/views/reports/hooks/use-reports-filters.hooks'
import { reportsSelector } from '@/state/reports/slices/index.slices'

import { dateTimeTableFormat } from '@/constants/date'
import { RoundsReportLibraryColumnProps } from '@/state/reports/models'
import ReportTableRow from './row.component'

import ReportTableHead from './head.component'

const ReportsTableComponent = () => {
  const { t } = useTranslation()

  const {
    sortedRows,
    createSortHandler,
    order,
    orderBy,
    headCells,
    headXlsxCells,
    roundsPagination,
    isLoading,
    getFormattedRow,
  } = useRoundsReports()

  const { handleChangePage, handleChangeLimit, downloadXlsxSearchFilter } =
    useReportsFilters()

  const { filters } = useSelector(reportsSelector, shallowEqual)

  const fileName = `rounds-${format(new Date(), dateTimeTableFormat)}`

  const xlsxButton = (
    <Button
      onClick={() =>
        downloadXlsxSearchFilter(
          { ...filters, page: 1, limit: 999999, xlsx: true },
          headXlsxCells,
          fileName,
          getFormattedRow,
        )
      }
      variant="contained"
    >
      {t('labels.downloadAsXLS')}
    </Button>
  )

  return (
    <>
      <TableContainer>
        <Table>
          <ReportTableHead
            order={order}
            orderBy={orderBy}
            createSortHandler={createSortHandler}
            headCells={headCells}
          />
          <TableBody>
            {!isLoading &&
              sortedRows.length > 0 &&
              sortedRows.map((row: RoundsReportLibraryColumnProps) => {
                return <ReportTableRow key={uuidv4()} row={row} />
              })}
          </TableBody>
        </Table>
      </TableContainer>
      {isLoading ? (
        <SkeletonLoader element="table" />
      ) : (
        <>
          {sortedRows.length !== 0 ? (
            <>
              <PaginationTable
                pagination={roundsPagination}
                leftContent={xlsxButton}
                handleChangePage={handleChangePage}
                handleChangeLimit={handleChangeLimit}
              />
            </>
          ) : (
            <Box
              display="flex"
              flexDirection="column"
              flexGrow={1}
              justifyContent="center"
              border={1}
              borderColor={alpha(colors.white, 0.3)}
              py={5}
            >
              <NoContentAvailable
                title={t('noResultsFound')}
                instruction={t('noResultsFoundCaption')}
              />
            </Box>
          )}
        </>
      )}
    </>
  )
}

export default ReportsTableComponent
